/**
 * Rapid X - WebSocket Client Hook
 * Real-time updates with JWT authentication and exponential backoff reconnection.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './auth';

const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_BASE = `${WS_PROTOCOL}//${window.location.host}`;

// Reconnection config
const MIN_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const RECONNECT_MULTIPLIER = 1.5;

export function useWebSocket(onMessage) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimeout = useRef(null);
  const [connected, setConnected] = useState(false);
  const [channels, setChannels] = useState([]);

  const connect = useCallback(() => {
    if (!user) return;
    
    // Get token from localStorage
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const wsUrl = `${WS_BASE}/api/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[WS] Connected');
        setConnected(true);
        reconnectAttempt.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle connection confirmation
          if (data.type === 'connected') {
            setChannels(data.channels || []);
            console.log('[WS] Channels:', data.channels);
            return;
          }
          
          // Handle pong
          if (data.type === 'pong') return;
          
          // Forward message to handler
          if (onMessage) {
            onMessage(data);
          }
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('[WS] Closed:', event.code, event.reason);
        setConnected(false);
        setChannels([]);
        wsRef.current = null;
        
        // Don't reconnect on auth errors
        if (event.code === 4001) {
          console.log('[WS] Auth failed, not reconnecting');
          return;
        }
        
        // Exponential backoff reconnect
        const delay = Math.min(
          MIN_RECONNECT_DELAY * Math.pow(RECONNECT_MULTIPLIER, reconnectAttempt.current),
          MAX_RECONNECT_DELAY
        );
        reconnectAttempt.current++;
        
        console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempt.current})`);
        reconnectTimeout.current = setTimeout(connect, delay);
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('[WS] Connection error:', e);
    }
  }, [user, onMessage]);

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    connect();

    // Ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    reconnectAttempt.current = 0;
    connect();
  }, [connect]);

  return {
    connected,
    channels,
    reconnect,
  };
}

export default useWebSocket;
